import { useRef } from 'react';
import { createPortal } from 'react-dom';
import type { ReactNode } from 'react';
import { cx } from '../lib/cx';
import { Icon } from '../icons/Icon';
import { useFocusTrap, useLockBodyScroll, useOnEscape } from '../lib/hooks';

export interface ModalProps {
  /** Defaults to true so the modal can be conditionally rendered by the parent. */
  open?: boolean;
  onClose: () => void;
  title?: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  /** Close when the backdrop is clicked. Default true. */
  closeOnBackdrop?: boolean;
  /** Hide the header ✕. */
  hideClose?: boolean;
  /** Accessible name for the ✕. English default; the app passes a translation. */
  closeLabel?: string;
  children?: ReactNode;
  'aria-label'?: string;
}

/** Centered modal over a dimmed backdrop. Traps focus, restores it on close,
 *  closes on Escape / backdrop, and locks body scroll (brief §4.2). */
export function Modal({
  open = true,
  onClose,
  title,
  footer,
  size = 'md',
  closeOnBackdrop = true,
  hideClose = false,
  closeLabel = 'Close',
  children,
  ...rest
}: ModalProps) {
  const ref = useRef<HTMLDivElement>(null);
  useOnEscape(onClose, open);
  useLockBodyScroll(open);
  useFocusTrap(ref, open);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div className="tc-overlay" onMouseDown={closeOnBackdrop ? onClose : undefined}>
      <div
        ref={ref}
        className={cx('tc-modal', size !== 'md' && `tc-modal--${size}`)}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : rest['aria-label']}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {(title || !hideClose) && (
          <header className="tc-modal__head">
            {title ? <h2 className="tc-modal__title">{title}</h2> : <span />}
            {!hideClose && (
              <button type="button" className="tc-modal__close" aria-label={closeLabel} onClick={onClose}>
                <Icon name="x" size={18} />
              </button>
            )}
          </header>
        )}
        <div className="tc-modal__body">{children}</div>
        {footer && <div className="tc-modal__foot">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}
